// @flow
import React, { Component } from 'react';

import { connect } from 'react-redux';
import moment from 'moment';
import { withTranslation, WithTranslation, Trans } from 'react-i18next';

import AppView from '../layouts/AppView';
import TextInput from '../shared/TextInput';
import Button from '../shared/Button';
import Text from '../shared/Typography';
import Loader from '../shared/Loader';
import Icon from '../shared/Icon';
import AuthError from '../shared/AuthError';
import shareService from '../../services/record-sharing-service';
import websocketService from '../../services/websocket-service';
import { createResourceModel, isModel } from '../../utils/health-resources';
import validate from '../../validators/record-share-create';
import { getRouteTFunction } from '../../i18n';
import { generateKey } from '../../utils/utils';

import illoSuccess from '../../assets/illustrations/share-success.png';
import illoError from '../../assets/illustrations/share-error.png';


type Props = {
  match: {
    params: {
      token: string
    }
  },
  history: Object,
  loading: boolean,
  location?: {
    search?: string,
    state?: {
      selectedResources: any[]
    }
  },
} & WithTranslation

type State = {
  loading: boolean,
  error: string,
  shareDuration: string,
  step: number,
  recipientEmails: string[],
  selectedResources: any[]
}

const NUM_SHARE_DURATION = 1; // the possible number of days, weeks or months to share the record for

class RecordShareCreate extends Component<Props, State> {
  state = {
    error: '',
    loading: false,
    step: 1,
    shareDuration: null,
    recipientEmails: [''],
    emailErrors: [''], // indices correspond to recipientEmails array
    shareSuccess: false,
    shareFailure: false,
    shareStatusesMap: {}, // { recipientEmail: { key, id, status } }
    selectedResources: []
  }

  state: State;
  props: Props;

  setRecipientEmail = (e: SyntheticInputEvent): void => {
    const { recipientEmails, emailErrors } = this.state;
    const localErrors = validate({
      email: { input: e.target.value, otherInputs: recipientEmails }
    });
    const indexToUpdate = e.target.name.split('_')[1];
    recipientEmails.splice(indexToUpdate, 1, e.target.value);
    emailErrors.splice(indexToUpdate, 1, localErrors);
    this.setState({ recipientEmails, emailErrors });
  }

  addEmail = () => {
    const { recipientEmails, emailErrors } = this.state;
    recipientEmails.push('');
    emailErrors.push('');
    this.setState({ recipientEmails, emailErrors });
  }

  removeEmail = (i) => {
    const { recipientEmails, emailErrors } = this.state;
    recipientEmails.splice(i, 1);
    emailErrors.splice(i, 1);
    this.setState({ recipientEmails, emailErrors });
  }

  setDuration = (newDuration) => {
    const currentDuration = this.state.shareDuration;
    if (!newDuration) return;
    if (currentDuration && currentDuration === newDuration) {
      this.setState({ shareDuration: null });
    } else {
      this.setState({ shareDuration: newDuration });
    }
  }

  back = (): void => {
    this.props.history.goBack();
  }

  getSelectedResources() {
    let resources = this.props.location && this.props.location.state && this.props.location.state.selectedResources;
    if (!resources) {
      if (this.state.selectedResources.length) {
        resources = this.state.selectedResources;
      } else {
        return [];
      }
    }
    return resources.map((resource) => (
      // Sometimes when refreshing the page, the resources don't get loaded in their model wrappers
      isModel(resource) ? resource : createResourceModel(resource.properties)
    ));
  }

  componentWillUnmount() {
    if (this.socket) this.socket.disconnect();
  }

  componentDidMount = async () => {
    const resources = this.getSelectedResources();
    const routesTFunction = getRouteTFunction();
    if (!resources.length) {
      this.props.history.push(routesTFunction('redirectPaths./dashboard'));
    }
    this.setState({ selectedResources: resources });
    this.socket = await websocketService.connect();
    this.socket.on('connect', () => {
      this.socket.on('share_records', async (data) => {
        const share = JSON.parse(data);
        this.updateShareStatus(share.key, share.status);
      });
    });
  }

  updateShareStatus = (key, status) => {
    const shareStatusesMap = this.state.shareStatusesMap;
    const emailToUpdate = Object.keys(shareStatusesMap).find((email) => shareStatusesMap[email].key === key);
    if (!emailToUpdate || !shareStatusesMap[emailToUpdate]) return;

    shareStatusesMap[emailToUpdate].status = status;
    // If error, delete the viewer
    if (status.includes('error') && shareStatusesMap[emailToUpdate].id) {
      shareService.delete(shareStatusesMap[emailToUpdate].id);
    }

    const statuses = Object.values(shareStatusesMap).map((s) => s.status);
    const shareSuccess = statuses.every((s) => s === 'success');
    const shareFailure = statuses.some((s) => s.includes('error')) && statuses.every((s) => s !== 'pending');

    this.setState({ shareStatusesMap, shareSuccess, shareFailure, loading: !(shareSuccess || shareFailure) });
  }

  shareRecords = (emails) => {
    const expiryDate = moment().add(NUM_SHARE_DURATION, `${this.state.shareDuration}s`);
    const sharedResources = [];
    this.getSelectedResources().forEach((r) => {
      if (r.aggregated) {
        sharedResources.push(...r.resources.map((resource) => resource.id));
      } else {
        sharedResources.push(r.id);
      }
    });
    // set (or reset) status to pending
    // assign a key to recognize each recipient by, simply the index of this recipient in the recipientEmails array
    const shareStatusesMap = this.state.shareStatusesMap || {};
    const emailKeys = [];
    emails.forEach((email) => {
      const emailKey = this.state.recipientEmails.indexOf(email);
      emailKeys.push(emailKey);
      shareStatusesMap[email] = { status: 'pending', key: emailKey };
    });
    this.setState({
      loading: true,
      shareStatusesMap
    });

    shareService.shareMultiple(
      { resource_ids: sharedResources,
        expiry_date: expiryDate,
        emails,
        email_keys: emailKeys
      },
      this.socket.id
    ).then((response) => {
      response.viewers.forEach((viewer) => {
        if (shareStatusesMap[viewer.email]) shareStatusesMap[viewer.email].id = viewer.id;
      });
      this.setState({
        step: null,
        shareSuccess: false,
        shareFailure: false,
        shareStatusesMap
      });
    }).catch((err) => {
      this.setState({
        step: null,
        loading: false,
        shareSuccess: false,
        shareFailure: true
      });
      console.log(err);
    });
  }

  header = ({ step, text }) => {
    const routesTFunction = getRouteTFunction();
    if (step) {
      return (
        <Text h1 className='record-share__title' align='center'>
          <button className='record-share__back-icon' onClick={() => this.setState({ step: step - 1 })}>
            <Icon size='medium' className={'icon ic-arrow-back is-purple'} />
          </button>
          {text}
        </Text>
      );
    }
    return (
      <Text h1 className='record-share__title' align='center'>
        <button
          className='record-share__back-icon'
          onClick={() => this.props.history.push(routesTFunction('redirectPaths./dashboard'))}
        >
          <Icon size='medium' className={'icon ic-arrow-back is-purple'} />
        </button>
        {text}
      </Text>
    );
  }

  step1Page = ({ recipientEmails, step }) => {
    const { emailErrors } = this.state;
    const { t } = this.props;
    return (
      <React.Fragment>
        <div className='record-share__verify__container is-center'>
          <this.header text={t('who_to_share_with')} />
          {
            recipientEmails.map((email, i) => (
              <div key={i}>
                <TextInput
                  id={`recipientEmail_${i}`}
                  type='string'
                  name={`recipientEmail_${i}`}
                  value={email}
                  label={t('email_label')}
                  onChange={this.setRecipientEmail}
                  required
                  showDeleteButton={i !== 0}
                  error={emailErrors[i] && emailErrors[i].length > 0}
                  onDelete={() => this.removeEmail(i)}
                />
                {emailErrors[i] && emailErrors[i].length > 0 && emailErrors[i].map((err) => (
                  <AuthError key={generateKey()} error={err} />
                ))}
              </div>
            ))
          }
          <span
            role='button'
            className='type-link record-share__add-email'
            onClick={this.addEmail}
            tabIndex={0}
          >
            <Text icon='profile-add' iconSize='small' link>{t('add_email')}</Text>
          </span>
          <div className='record-share__verify__container is-center'>
            <Button
              id='submit'
              className='is-primary'
              label={t('continue')}
              isDisabled={
                this.state.recipientEmails.some((email) => !email || !email.length) ||
                emailErrors.some((errors) => errors.length)
              }
              onClick={() => this.setState({ step: step + 1 })}
            />
          </div>
        </div>
      </React.Fragment>
    );
  }

  durationCard = ({ currentDuration, newDuration }) => {
    const { t } = this.props;
    return (
      <button
        className={`card record-share__duration-card${currentDuration === newDuration ? '__selected' : ''}`}
        onClick={() => this.setDuration(newDuration)}
      >
        <Text paragraph>{t(newDuration, { count: NUM_SHARE_DURATION })}</Text>
      </button>
    );
  }

  step2Page = ({ shareDuration, step }) => {
    const { t } = this.props;
    const day = 'day';
    const week = 'week';
    const month = 'month';
    return (
      <React.Fragment>
        <div className='record-share__verify__container is-center'>
          <this.header step={step} text={t('how_long_access')} />
          <div align='center'>
            <this.durationCard currentDuration={shareDuration} newDuration={day} />
            <this.durationCard currentDuration={shareDuration} newDuration={week} />
            <this.durationCard currentDuration={shareDuration} newDuration={month} />
          </div>
          <div className='record-share__verify__container is-center'>
            <Button
              id='submit'
              className='is-primary'
              label={t('continue')}
              isDisabled={!shareDuration}
              onClick={() => this.setState({ step: step + 1 })}
            />
          </div>
        </div>
      </React.Fragment>
    );
  }

  step3Page = ({ recipientEmails, shareDuration, step }) => {
    const { t } = this.props;
    const routesTFunction = getRouteTFunction();
    const numRecipients = recipientEmails ? recipientEmails.length : 0;
    const numRecordsStr = (
      <Text span className='record-share__num'>
        {{ nRecords: t('record', { count: this.state.selectedResources.length }) }}
      </Text>);
    const numRecipientsStr =
      <Text span className='record-share__num'>{{ nRecipients: t('person', { count: numRecipients }) }}</Text>;
    const durationStr = <Text span className='record-share__num'>{{ l: 1, duration: t(shareDuration) }}</Text>;
    return (
      <React.Fragment>
        <div className='record-share__verify__container is-center'>
          <this.header step={step} text={t('does_this_look_right')} />
          <ul className='record-share'>
            <li>
              <Text h3 align='center'>
                <Trans
                  i18nKey='share_summary'
                  t={t}
                  components={[numRecordsStr, <br />, numRecipientsStr, durationStr]}
                />
              </Text>
            </li>
            {
              this.state.selectedResources.map((r) => {
                const url = routesTFunction('redirectPaths./resource/:id',
                                              r.aggregated ? { id: r.resource_type } : { id: r.id });
                return (
                  <li key={r.id}>
                    <span
                      role='button'
                      className='record-share__link'
                      onClick={() => window.open(url, '_blank')}
                      tabIndex={0}
                    >
                      <Text paragraph>{r.title}</Text>
                    </span>
                  </li>
                );
              })
            }
          </ul>
          <div className='record-share__verify__container is-center'>
            <Button
              id='submit'
              className='is-primary'
              label={t('confirm_share', { count: this.state.selectedResources.length })}
              isLoading={this.state.loading}
              onClick={() => this.shareRecords(recipientEmails)}
            />
          </div>
        </div>
      </React.Fragment>
    );
  }

  render() {
    const { loading, t } = this.props;
    const { shareDuration, step, recipientEmails, shareSuccess, shareFailure, shareStatusesMap } = this.state;
    const routesTFunction = getRouteTFunction();
    // const errorEmails = Object.keys(shareStatusesMap).filter((email) =>
    //   shareStatusesMap[email].status && shareStatusesMap[email].status.includes('error')
    // );
    if (loading) return <Loader isLoading />;
    return (
      <AppView viewType='private-body'>
        <div className='content-container'>
          <main className='record-share__verify'>
            {step === 1 && <this.step1Page recipientEmails={recipientEmails} step={step} />}
            {step === 2 && <this.step2Page shareDuration={shareDuration} step={step} />}
            {step === 3 &&
              <this.step3Page
                recipientEmails={recipientEmails}
                shareDuration={shareDuration}
                step={step}
              />}
            {shareSuccess &&
              <div className='record-share__verify__container is-center'>
                <img className='record-share__illo' src={illoSuccess} alt={t('record_alt')} />
                <Text h1 className='record-share__title' align='center'>{t('records_shared_heading')}</Text>
                <Text paragraph align='center'>{t('records_shared_msg')}</Text>
                <div className='record-share__verify__container is-center'>
                  <Button
                    className='is-primary'
                    label={t('back_to_dashboard')}
                    onClick={() => this.props.history.push(routesTFunction('redirectPaths./dashboard'))}
                  />
                </div>
              </div>
            }
            {!step && !shareSuccess &&
              <div className='record-share__verify__container is-center'>
                {shareFailure && <img className='record-share__illo' src={illoError} alt={t('record_alt')} />}
                {!shareFailure && <img className='record-share__illo' src={illoSuccess} alt={t('record_alt')} />}
                <Text h1 className='record-share__title' align='center'>
                  {shareFailure ? t('records_shared_err_heading') : t('records_shared_pending_heading')}
                </Text>
                {shareFailure &&
                  <Text paragraph align='center'>
                    {t('records_shared_err_msg')}
                  </Text>
                }
                <br />
                <ul className='record-share'>
                  {
                    recipientEmails.map((r) => (
                      <li key={r}>
                        {shareStatusesMap[r] && shareStatusesMap[r].status === 'success' &&
                          <Text icon='check--green' iconSize='small' paragraph>{r}</Text>
                        }
                        {shareStatusesMap[r] && shareStatusesMap[r].status === 'error' &&
                          <Text icon='close-red' iconSize='small' paragraph>{r}</Text>
                        }
                        {shareStatusesMap[r] && shareStatusesMap[r].status === 'pending' &&
                          <Text icon='pending' iconSize='small' paragraph>{r}</Text>
                        }
                      </li>
                    ))
                  }
                </ul>
              </div>
            }
          </main>
        </div>
      </AppView>
    );
  }
}

const mapStateToProps = (appState) => ({
  loading: appState.shared && appState.shared.loading
});

const mapDispatchToProps = () => ({
});

export default withTranslation('recordShareCreate')(
  connect(mapStateToProps, mapDispatchToProps)(RecordShareCreate)
);



// WEBPACK FOOTER //
// ./src/components/pages/record-share-create.js